import { useEffect } from "react";

/**
 * Automatically applies glass-shine to ALL buttons and shining-dots to
 * card/section-glow elements on landing pages. Uses MutationObserver to
 * catch dynamically rendered elements, IntersectionObserver to trigger once
 * on scroll into view.
 */
export function useGlassShine() {
  useEffect(() => {
    const observed = new WeakSet<Element>();

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;

          // Trigger glass shine on buttons
          if (el.classList.contains("btn-glass-shine")) {
            el.classList.add("shine-active");
          }

          // Trigger glow + dots on section-glow elements
          if (el.classList.contains("section-glow")) {
            el.classList.add("glow-visible");
            if (!el.classList.contains("card-dots") && !el.classList.contains("card-dots-right")) {
              el.classList.add("card-dots");
            }
          }

          io.unobserve(el);
        });
      },
      { threshold: 0.15 }
    );

    function isButtonLike(el: Element): boolean {
      // Exclude nav/header buttons
      if (el.closest("header") || el.closest("nav")) return false;
      // Exclude anything inside an accordion (FAQ section)
      // Exclude accordion triggers — Radix marks them with data-radix-collection-item
      if (el.hasAttribute("data-radix-collection-item") || el.closest("[data-radix-collection-item]")) return false;
      // Also exclude by checking for accordion item parent (border-b + data-state pattern)
      if (el.closest("[data-state][data-orientation]")) return false;
      if (el.tagName === "BUTTON") return true;
      if (el.tagName === "A" && el.getAttribute("role") === "button") return true;
      if (
        el.tagName === "A" &&
        el.className &&
        (el.className.includes("inline-flex") ||
          el.className.includes("btn") ||
          el.className.includes("gradient-primary"))
      ) {
        return true;
      }
      return false;
    }

    function processElement(el: Element) {
      if (observed.has(el)) return;

      // Auto-tag buttons inside <section> elements
      if (isButtonLike(el) && el.closest("section")) {
        if (!el.classList.contains("btn-glass-shine")) {
          el.classList.add("btn-glass-shine");
        }
      }

      // Observe glass-shine and section-glow elements
      if (
        el.classList.contains("btn-glass-shine") ||
        el.classList.contains("section-glow")
      ) {
        observed.add(el);
        io.observe(el);
      }
    }

    function observeAll(root: ParentNode) {
      // Buttons & link-buttons inside sections
      root
        .querySelectorAll("section button, section a")
        .forEach(processElement);
      // Existing tagged elements
      root
        .querySelectorAll(".btn-glass-shine, .section-glow")
        .forEach(processElement);
    }

    // Initial scan
    observeAll(document);

    // Watch for new elements
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node instanceof HTMLElement) {
            processElement(node);
            observeAll(node);
          }
        }
      }
    });

    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, []);
}
